import { cutApi, CutAPI } from '@api/cut';
import dayjs from 'dayjs';
import { Button, Checkbox, Grid, Link, Paper, Typography } from '@mui/material';
import { SubBar } from '@components/SubBar';
import { Hint } from '@components/Hint';
import { LastUpdatedText } from '@components/LastUpdatedText';
import { CutActivityFilters } from '@components/CutActivity/CutActivityFilters';
import { ScrollToTop } from '@components/ScrollToTop';
import { useEffect, useMemo, useState } from 'react';
import { useStyles } from './styles/CutActivity.styles';
import { useSelector } from 'react-redux';
import { SortDirections } from '@/types/sort';
import { dateFormat, getMonthInfo } from '@utils/dates';
import { getFilterParams } from '@utils/filters';
import { transformCuts } from '@utils/data';
import { CutFilterOptions } from '@/types/cuts';
import { useFetch } from '@hooks/useFetch';
import { useOrgs } from '@hooks/useOrgs';
import { MultiSelectFilter } from '@components/MultiSelectFilter';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { CUT_ACTIVITY_COLUMNS, SearchIDType } from '@consts/cuts';
import { CutAction } from '@components/CutActivity/CutAction';
import { useAlert } from '@hooks/useAlert';

import type { Cut, CutFilter, ParsedCut } from '@/types/cuts';
import type { CutStatus } from '@consts/cut';
import type { RootState } from '@features/redux/store';
import { AppRoutes } from '@consts/routes';
import { ExportIcon } from '@assets/index';
import { getDeviceType, openPopup } from '@utils/index';
import { type ColumnOptions, DataTable } from '@components/tables/DataTable';
import { useSearchParams } from 'react-router-dom';
import { DownloadModels } from '@components/buttons/DownloadModels';

const PAGE_SIZE = 50;
const DATE_LIMIT = 12; // in months

const DEFAULT_COLUMN_FILTER_STATE = CUT_ACTIVITY_COLUMNS.reduce((acc, { field }) => {
    acc[field] = true;
    return acc;
}, {} as Record<string, boolean>);

const INIT_FILTER_STATE = {
    [CutFilterOptions.CutType]: {},
    [CutFilterOptions.Material]: {},
    [CutFilterOptions.Country]: {},
    [CutFilterOptions.Company]: {},
    [CutFilterOptions.Reseller]: {},
    [CutFilterOptions.Location]: {},
    [CutFilterOptions.Status]: {},
};

// todo: Migrate CutActivity over to use DataTable
export const CutActivity = () => {
    const classes = useStyles();
    const {
        IdToken,
        Roles: { role },
    } = useSelector((state: RootState) => state.auth);
    const { companies, resellers, locations, ...rest } = useOrgs();
    const { setAlert } = useAlert();

    const [searchParams, setSearchParams] = useSearchParams();

    const [searchInfo, setSearchInfo] = useMemo(() => {
        const search = searchParams.get('search');
        const type = searchParams.get('type');

        const info = {
            id: search || '',
            type:
                type && Object.values(SearchIDType).includes(type as any) ? (type as SearchIDType) : SearchIDType.CutID,
        };

        const setInfo = ({ id: search, type }: typeof info) => {
            setSearchParams(new URLSearchParams({ search, type }));
        };

        return [info, setInfo] as const;
    }, [searchParams, setSearchParams]);

    const [searchError, setSearchError] = useState('');

    const { firstDay } = getMonthInfo(1);
    const defaultRange = { startDate: dateFormat(firstDay), endDate: dateFormat() };
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(defaultRange);

    const [lastUpdated, setLastUpdated] = useState<string>('');
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [numberRecords, setNumberRecords] = useState<number>(0);

    const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.DESC);
    const [filters, setFilters] = useState<CutFilter>(INIT_FILTER_STATE);

    const setFilter = (key: CutFilterOptions, value: string, selectOne?: boolean) => {
        const isActive = filters[key][value];
        setFilters({ ...filters, [key]: { ...(selectOne ? {} : filters[key]), [value]: !isActive } });
    };

    const uri = searchInfo.id ? CutAPI.Search : CutAPI.Activity;

    const params = searchInfo.id
        ? {
              searchID: searchInfo.id,
              searchIDType: searchInfo.type,
              pageNum: Number.isNaN(pageNumber) ? 0 : pageNumber,
              pageSize: PAGE_SIZE,
          }
        : {
              ...dateRange,
              ...getFilterParams(filters),
              sortDirection,
              pageNum: Number.isNaN(pageNumber) ? 0 : pageNumber,
              pageSize: PAGE_SIZE,
          };

    const { data, loading, refetch, setData } = useFetch({
        spinner: false,
        fetcher: (input?: { uri: CutAPI; params: Record<string, any> }) => {
            return cutApi(input?.uri || uri, 'GET')({ token: IdToken!, params: input?.params || params }) as Promise<
                Cut[]
            >;
        },
        defaultValue: [] as Cut[],
        errorHandler: () => {
            setData([]);
            setAlert('No results - an error occurred.', 'error');
        },
    });

    const [status, setStatus] = useState<CutStatus | ''>('');
    const [selectedCuts, setSelectedCuts] = useState<Record<string, ParsedCut>>({});

    useEffect(() => {
        refetch();
    }, [sortDirection, pageNumber]);

    // * This is a temporary fix
    // * to force table data to reload if the search params
    // * have been automatically populated (via a Link)
    // ? We do this because React Router will not re-render a
    // ? component if it links to itself, even if the search params
    // ? are different from the previous.
    // ! We don't want to reload the entire page, as that forces
    // ! a fresh fetch of OrgMap, countries data, etc.
    useEffect(() => {
        if (!searchParams.has('auto')) return;

        refetch();
        setSearchParams((prev) => {
            prev.delete('auto');
            return prev;
        });
    }, [searchParams]);

    useEffect(() => {
        const [cut] = data;
        const recordCount = cut ? +cut.totalCutsInQuery : 0;
        setNumberRecords(recordCount);
        setLastUpdated(dayjs().format('hh:mm:ssa - MM/DD/YYYY'));
    }, [data]);

    const mappedData = useMemo(
        () => transformCuts(data, companies, locations, resellers),
        [data, companies, locations, resellers]
    );

    const dateRangeError = useMemo(
        () => dayjs(dateRange.startDate).add(DATE_LIMIT, 'month').diff(dayjs(dateRange.endDate)) < 0,
        [dateRange]
    );

    const [columnFilter, setColumnFilter] = useLocalStorage({
        value: DEFAULT_COLUMN_FILTER_STATE,
        key: 'cut-activity-columns',
    });

    const columns = useMemo<ColumnOptions<ParsedCut>[]>(() => {
        const result = CUT_ACTIVITY_COLUMNS.filter(({ field }) => Boolean(columnFilter[field]));
        if (role.type.finance || role.type.support)
            result.push({
                field: 'dbCutId',
                headerName: 'Select',
                width: 60,
                renderExtraCellContent: (row) => {
                    const isSelected = row.cutId in selectedCuts;

                    return (
                        <Checkbox
                            checked={isSelected}
                            onChange={() => {
                                setSelectedCuts((prev) => {
                                    if (isSelected) {
                                        const copy = { ...prev };
                                        delete copy[row.cutId];
                                        return copy;
                                    }

                                    return {
                                        ...prev,
                                        [row.cutId]: row as any as ParsedCut,
                                    };
                                });
                            }}
                        />
                    );
                },
                ignoreValue: true,
            });
        return result;
    }, [columnFilter, selectedCuts]);

    const handleFilterClear = () => {
        setSortDirection(SortDirections.DESC);
        setPageNumber(0);
        setDateRange(defaultRange);
        setFilters({ ...INIT_FILTER_STATE });
        setSearchInfo({
            id: '',
            type: SearchIDType.CutID,
        });
        setSearchError('');

        // Refetch all cuts just as they were first fetched
        refetch({
            uri: CutAPI.Activity,
            params: {
                ...defaultRange,
                ...getFilterParams(INIT_FILTER_STATE),
                sortDirection: SortDirections.DESC,
                pageNum: 0,
                pageSize: PAGE_SIZE,
            },
        });
    };

    const handleExport = () => {
        // obtain active columns
        const columns = Object.entries(columnFilter)
            .filter(([, value]) => value)
            .map(([key]) => key);

        openPopup(`${AppRoutes.ExportActivity}?params=${JSON.stringify(params)}&columns=${JSON.stringify(columns)}`, {
            width: 400,
            height: 400,
        });
    };

    const handleSearch = () => {
        if (pageNumber === 0) {
            refetch();
            return;
        }

        setPageNumber(0);
    };

    // ! Dont include cutDate in sortField if its not in the columnFilter
    const sortField = useMemo(
        () =>
            columnFilter['cutDate'] ? [{ field: 'cutDate', sort: sortDirection.toLowerCase() as 'asc' | 'desc' }] : [],
        [columnFilter]
    );

    return (
        <div className={classes.root}>
            <ScrollToTop />

            <SubBar className={classes.subMenuBar}>
                <Grid
                    container
                    display={'flex'}
                    flex={1}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    className={classes.lastUpdatedTextWrapperContainer}>
                    <LastUpdatedText lastUpdated={lastUpdated || 'N/A'} />
                </Grid>
            </SubBar>

            <Typography className={classes.title}>Cut Activity</Typography>

            <Hint
                text={
                    <>
                        Below is the cut activity across all of your RapidCut Systems. If you are looking for invoices
                        related to your cut activity, all invoices can be found in{' '}
                        <Link href='https://account.moutfitters.com/' target='_blank'>
                            Account Central
                        </Link>
                        .
                    </>
                }
            />

            <Paper sx={{ marginTop: '40px', padding: '20px', minWidth: '350px' }} elevation={4}>
                {/** Cut Filters and Export button Here */}

                <Grid container className={classes.filterContainer}>
                    <Typography fontSize='12px' marginTop='8px'>
                        Filters
                    </Typography>

                    <CutActivityFilters
                        onSearch={handleSearch}
                        orgs={{ companies, resellers, locations, ...rest }}
                        dateRange={dateRange}
                        dateRangeError={dateRangeError ? `Max Range is ${DATE_LIMIT} months` : ''}
                        filters={filters}
                        companies={companies}
                        locations={locations}
                        resellers={resellers}
                        setFilter={setFilter}
                        searchID={searchInfo.id}
                        searchIDType={searchInfo.type}
                        searchIDError={searchError}
                        setSearchIDError={setSearchError}
                        setSearchID={(id) => setSearchInfo({ ...searchInfo, id })}
                        setSearchIDType={(type) => setSearchInfo({ ...searchInfo, type })}
                        setDateRange={setDateRange}
                    />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}>
                        <Button
                            variant={'contained'}
                            size={'medium'}
                            disabled={dateRangeError || Boolean(searchError)}
                            onClick={handleSearch}>
                            Search
                        </Button>

                        {getDeviceType(navigator.userAgent) === 'desktop' && (
                            <Button
                                startIcon={<ExportIcon />}
                                color='orange'
                                variant='contained'
                                size='medium'
                                onClick={handleExport}>
                                Export
                            </Button>
                        )}

                        <MultiSelectFilter
                            search
                            margin={false}
                            sort={false}
                            label='Columns'
                            options={CUT_ACTIVITY_COLUMNS}
                            idKey='field'
                            value={columnFilter}
                            getLabelValue={({ headerName }) => headerName!}
                            onChange={(data) => {
                                setColumnFilter((prev) => {
                                    // The current number of selected columns
                                    const count = Object.values(prev).filter(Boolean).length;
                                    // Whether or not the target option is currently selected
                                    const selected = prev[data];

                                    // If the user is trying to deselect the target option, but it is
                                    // the only option currently selected, ignore the change.
                                    if (selected && count <= 1) {
                                        return prev;
                                    }

                                    return {
                                        ...prev,
                                        [data]: !selected,
                                    };
                                });
                            }}
                        />

                        <Button
                            variant={'text'}
                            size={'small'}
                            className={classes.clearFilterButton}
                            onClick={handleFilterClear}>
                            <u>Clear All Filters</u>
                        </Button>
                    </div>
                </Grid>

                <DataTable
                    columnOrdering
                    storageKey='Cut-Activity'
                    loading={loading}
                    virtualize={false}
                    columns={columns}
                    rows={mappedData}
                    getUniqueRowId={({ cutId, dbCutId }) => `${dbCutId}-${cutId}`}
                    pageSize={PAGE_SIZE}
                    totalRowCount={numberRecords}
                    page={pageNumber + 1}
                    onPaginate={(page) => setPageNumber(page - 1)}
                    pinnedColumns={{
                        right: ['dbCutId'],
                    }}
                    onSortModelChange={() => {
                        setSortDirection((prev) =>
                            prev === SortDirections.DESC ? SortDirections.ASC : SortDirections.DESC
                        );
                    }}
                    sortModel={sortField}
                />

                <div className={classes.pagination}>
                    {(role.type.finance || role.type.support) && (
                        <CutAction
                            status={status}
                            setStatus={setStatus}
                            selectedCuts={selectedCuts}
                            setSelectedCuts={setSelectedCuts}
                            role={role}
                            onActionSuccess={refetch}
                        />
                    )}

                    <DownloadModels />
                </div>
            </Paper>
        </div>
    );
};
